import React, { useEffect, useState } from "react";
import { confirmable } from "react-confirm";
import { Modal, Input, Spin, Switch, InputNumber, Select } from "antd";
import { isEmpty } from "utils/GlobalFunctions";
import { apiGetAllUnits } from "services/shopUnitService";
import "pages/ShopProductListPage/AddProductDialog/ShopDialog.css";

function AddUnitDialog(props) {
  const { show, proceed, record, title } = props;

  const [data, setData] = useState({});
  const [errorField, setErrorField] = useState([]);
  const [isBusy] = useState(false);
  const [baseUnits, setBaseUnits] = useState([]);

  useEffect(() => {
    apiGetAllUnits()
      .then((units) => setBaseUnits((units || []).filter((u) => u.is_base === "1")))
      .catch(() => setBaseUnits([]));
  }, []);

  useEffect(() => {
    if (record !== undefined) {
      setData({
        ...record,
        is_base: record.is_base === "1",
        is_weight_base: record.is_weight_base === "1",
        base_unit_id: record.base_unit_id || null,
        factor: Number(record.factor || 1),
        status: record.status === "1",
      });
    } else {
      setData({ is_base: false, is_weight_base: false, base_unit_id: null, factor: 1, status: true });
    }
  }, [record?.id]);

  const onChangeValue = (field, value) => {
    setData({ ...data, [field]: value });
    setErrorField(errorField.filter((x) => x !== field));
  };

  // Base units to offer this unit as a conversion target — a unit being
  // edited can't be its own base, so it's excluded from the list.
  const availableBaseUnits = baseUnits.filter((u) => !record || u.id !== record.id);

  const validateFields = () => {
    var errorList = Array();
    errorList = isEmpty(data, "name", errorList);
    if (!data.is_base) {
      if (!data.base_unit_id) errorList = [...errorList, "base_unit_id"];
      if (!data.factor || Number(data.factor) <= 0) errorList = [...errorList, "factor"];
    }
    setErrorField([...errorList]);
    return errorList.length == 0;
  };

  return (
    <Modal
      open={show}
      destroyOnClose={true}
      onCancel={() => proceed(null)}
      closable={true}
      maskClosable={false}
      title={title}
      className="shop-modal"
      width={480}
      okText="Save"
      onOk={() => {
        if (validateFields() === false) return;
        proceed({ ...data });
      }}
    >
      <Spin spinning={isBusy}>
        <div>
          <div className="shop-switch-row">
            <label className="shop-field-label" style={{ marginBottom: 0 }}>
              Active
            </label>
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Hidden"
              onChange={(v) => onChangeValue("status", v)}
              checked={!!data.status}
            />
          </div>
          <div className="shop-field">
            <label className="shop-field-label">
              Name<span className="req">*</span>
            </label>
            <Input
              placeholder="e.g. g, kg, ml, L, pcs"
              value={data.name || ""}
              status={errorField.includes("name") ? "error" : ""}
              onChange={(e) => onChangeValue("name", e.target.value)}
            />
          </div>
          <div className="shop-switch-row">
            <label className="shop-field-label" style={{ marginBottom: 0 }}>
              Base unit
            </label>
            <Switch checked={!!data.is_base} onChange={(v) => onChangeValue("is_base", v)} />
          </div>
          {data.is_base ? (
            <>
              <div style={{ fontSize: 12, color: "var(--darkGreyColor)", marginBottom: 12 }}>
                A base unit (like g, ml, pcs) is a fundamental unit — nothing else converts
                into it, and it doesn't convert into anything else.
              </div>
              <div className="shop-switch-row" style={{ marginBottom: 0 }}>
                <label className="shop-field-label" style={{ marginBottom: 0 }}>
                  Used for shipping weight calculations
                </label>
                <Switch checked={!!data.is_weight_base} onChange={(v) => onChangeValue("is_weight_base", v)} />
              </div>
              <div style={{ fontSize: 12, color: "var(--darkGreyColor)", marginTop: 4 }}>
                {data.is_weight_base
                  ? "This base unit (and anything that converts to it, like kg) counts toward shipping weight — 1 of this unit = 1 gram equivalent."
                  : "This base unit isn't treated as mass — variants using it (or units derived from it) show \"quotation required\" for weight-based shipping."}
                {" "}More than one base unit can be a weight unit at the same time (e.g. "g" for true mass and "ml" as a
                water-density approximation) — each converts to grams independently, so turning this on for one never
                turns it off for another. Only mark a unit this way if 1 of it genuinely equals about 1 gram.
              </div>
            </>
          ) : (
            <>
              <div className="shop-field">
                <label className="shop-field-label">
                  Converts to<span className="req">*</span>
                </label>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Choose a base unit"
                  status={errorField.includes("base_unit_id") ? "error" : ""}
                  value={data.base_unit_id || undefined}
                  onChange={(v) => onChangeValue("base_unit_id", v)}
                  options={availableBaseUnits.map((u) => ({ value: u.id, label: u.name }))}
                />
              </div>
              <div className="shop-field" style={{ marginBottom: 0 }}>
                <label className="shop-field-label">
                  Factor<span className="req">*</span>
                </label>
                <InputNumber
                  style={{ width: "100%" }}
                  min={0.0001}
                  step={0.0001}
                  status={errorField.includes("factor") ? "error" : ""}
                  value={data.factor}
                  onChange={(v) => onChangeValue("factor", v)}
                />
                <div style={{ fontSize: 12, color: "var(--darkGreyColor)", marginTop: 4 }}>
                  How many of the chosen base unit one of this unit equals — e.g. "kg" converts
                  to "g" with a factor of 1000.
                </div>
              </div>
            </>
          )}
        </div>
      </Spin>
    </Modal>
  );
}

export default confirmable(AddUnitDialog);
